import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Eye, UsersThree, ChartLineUp, Funnel } from "@phosphor-icons/react";

const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-6" data-testid={`analytics-kpi-${label}`}>
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white">
        <Icon size={24} weight="duotone" />
      </div>
      <div>
        <p className="font-heading text-3xl font-bold text-ink">{value ?? 0}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 font-heading text-sm font-semibold text-ink">{title}</h3>
      <div className="h-64">{children}</div>
    </div>
  );
}

export function AnalyticsDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch((e) => console.error("Failed to load analytics:", e));
  }, []);

  if (!data) return <p className="py-10 text-center text-slate-400" data-testid="analytics-loading">Loading analytics…</p>;

  const { totals, visits_by_day, leads_by_day, top_pages, lead_totals } = data;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={UsersThree} label="Visitors (30d)" value={totals.visitors_30d} />
        <Kpi icon={Eye} label="Page Views (30d)" value={totals.views_30d} />
        <Kpi icon={ChartLineUp} label="Views Today" value={totals.views_today} />
        <Kpi icon={Funnel} label="Leads (30d)" value={totals.leads_30d} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Visitors & Page Views — last 30 days">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={visits_by_day} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E73BE" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#1E73BE" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#16B894" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#16B894" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} interval={6} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDate} />
              <Area type="monotone" dataKey="views" name="Page views" stroke="#1E73BE" fill="url(#gViews)" strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#16B894" fill="url(#gVisitors)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Leads captured — last 30 days">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={leads_by_day} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} interval={6} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={shortDate} />
              <Bar dataKey="leads" name="Leads" fill="#16B894" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="analytics-top-pages">
          <h3 className="mb-4 font-heading text-sm font-semibold text-ink">Top pages (30d)</h3>
          {top_pages.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No visits recorded yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {top_pages.map((p) => (
                <li key={p.path} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="truncate text-slate-700">{p.path === "/" ? "/ (Home)" : p.path}</span>
                  <span className="ml-4 shrink-0 rounded bg-mist px-2 py-0.5 text-xs font-semibold text-slate-600">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="analytics-lead-breakdown">
          <h3 className="mb-4 font-heading text-sm font-semibold text-ink">Lead breakdown (all time)</h3>
          <ul className="divide-y divide-slate-100">
            {Object.entries(lead_totals).map(([label, count]) => (
              <li key={label} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-slate-700">{label}</span>
                <span className="ml-4 shrink-0 rounded bg-mist px-2 py-0.5 text-xs font-semibold text-slate-600">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Visits are tracked first-party on this site. Conversion events (quotes, bookings, WhatsApp clicks) also fire to Google Analytics / Tag Manager.
      </p>
    </div>
  );
}
